import {
  NODE_W, NODE_H, NODE_W_ROOT, NODE_H_ROOT, NODE_RX,
  CONNECTION,
  EXPORT,
} from '../constants';
import { getNodeColors } from './colors';
import type { Node, Connection, PostIt } from '../types';

const POST_IT_EXPORT_W = 220;
const POST_IT_EXPORT_MARGIN = 20;
const POST_IT_COLORS: Record<string, { fill: string; stroke: string; text: string }> = {
  yellow: { fill: '#fef9c3', stroke: '#d97706', text: '#92400e' },
  green:  { fill: '#dcfce7', stroke: '#16a34a', text: '#14532d' },
  red:    { fill: '#fee2e2', stroke: '#ef4444', text: '#7f1d1d' },
};

const calcPostItLines = (ctx: CanvasRenderingContext2D, text: string, maxW: number): string[] => {
  if (!text) return [];
  ctx.font = '13px system-ui, sans-serif';
  const allLines: string[] = [];
  for (const para of text.split('\n')) {
    const words = para.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) continue;
    let current = '';
    for (const word of words) {
      const test = current ? `${current} ${word}` : word;
      if (ctx.measureText(test).width > maxW && current) {
        allLines.push(current);
        current = word;
      } else {
        current = test;
      }
    }
    if (current) allLines.push(current);
  }
  return allLines;
};

export const exportToPNG = (nodes: Node[], connections: Connection[], postIts: PostIt[] = []): void => {
  if (nodes.length === 0 && postIts.length === 0) return;

  const padding = 60;
  const getW = (n: Node) => n.type === 'problem' ? NODE_W_ROOT : NODE_W;
  const getH = (n: Node) => n.type === 'problem' ? NODE_H_ROOT : NODE_H;

  const nodeMinX = nodes.length ? Math.min(...nodes.map(n => n.x - getW(n) / 2)) - padding : 0;
  const nodeMinY = nodes.length ? Math.min(...nodes.map(n => n.y - getH(n) / 2)) - padding : 0;
  const nodeMaxX = nodes.length ? Math.max(...nodes.map(n => n.x + getW(n) / 2)) + padding : 0;
  const nodeMaxY = nodes.length ? Math.max(...nodes.map(n => n.y + getH(n) / 2)) + padding : 0;
  const minX = nodeMinX;
  const minY = nodeMinY;
  const nodesWidth = nodeMaxX - nodeMinX;
  const nodesHeight = nodeMaxY - nodeMinY;

  const tmpCanvas = document.createElement('canvas');
  const tmpCtx = tmpCanvas.getContext('2d')!;
  const maxTextW = POST_IT_EXPORT_W - 24;
  const lineH = 19;
  const postItPadV = 14;

  const postItLayouts = postIts.map(p => {
    const lines = calcPostItLines(tmpCtx, p.text, maxTextW);
    const h = Math.max(60, lines.length * lineH + postItPadV * 2);
    return { lines, h };
  });

  const hasPostIts = postIts.length > 0;
  const colW = hasPostIts ? POST_IT_EXPORT_W + POST_IT_EXPORT_MARGIN * 2 : 0;
  const totalPostItH = postItLayouts.reduce((acc, l) => acc + l.h + POST_IT_EXPORT_MARGIN, POST_IT_EXPORT_MARGIN);

  const width = nodesWidth + colW;
  const height = Math.max(nodesHeight, totalPostItH);

  const canvas = document.createElement('canvas');
  canvas.width = width * EXPORT.PNG_SCALE;
  canvas.height = height * EXPORT.PNG_SCALE;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(EXPORT.PNG_SCALE, EXPORT.PNG_SCALE);

  const isLatte = document.documentElement.classList.contains('latte');
  ctx.fillStyle = isLatte ? '#e6e9ef' : '#181825';
  ctx.fillRect(0, 0, width, height);

  connections.forEach(conn => {
    const fromNode = nodes.find(n => n.id === conn.from);
    const toNode = nodes.find(n => n.id === conn.to);
    if (!fromNode || !toNode) return;

    const dx = toNode.x - fromNode.x;
    const W_from = fromNode.type === 'problem' ? NODE_W_ROOT : NODE_W;
    const W_to = toNode.type === 'problem' ? NODE_W_ROOT : NODE_W;

    const fromPt = dx >= 0
      ? { x: fromNode.x + W_from / 2, y: fromNode.y }
      : { x: fromNode.x - W_from / 2, y: fromNode.y };
    const toPt = dx >= 0
      ? { x: toNode.x - W_to / 2, y: toNode.y }
      : { x: toNode.x + W_to / 2, y: toNode.y };

    const tension = Math.abs(dx) * 0.45;
    const cp1x = fromPt.x + (dx >= 0 ? tension : -tension);
    const cp2x = toPt.x - (dx >= 0 ? tension : -tension);

    const opacity = fromNode.banned || toNode.banned ? CONNECTION.BANNED_OPACITY : CONNECTION.OPACITY;
    const lineColor = conn.color || CONNECTION.COLOR;

    ctx.save();
    ctx.globalAlpha = opacity;
    ctx.strokeStyle = lineColor;
    ctx.lineWidth = CONNECTION.WIDTH;
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.moveTo(fromPt.x - minX, fromPt.y - minY);
    ctx.bezierCurveTo(cp1x - minX, fromPt.y - minY, cp2x - minX, toPt.y - minY, toPt.x - minX, toPt.y - minY);
    ctx.stroke();
    ctx.restore();
  });

  nodes.forEach(node => {
    const x = node.x - minX;
    const y = node.y - minY;
    const isProblem = node.type === 'problem';
    const W = isProblem ? NODE_W_ROOT : NODE_W;
    const H = isProblem ? NODE_H_ROOT : NODE_H;

    const { fillColor, textColor, outlineColor, outlineWidth, shadowColor } = getNodeColors(node);

    ctx.save();
    ctx.globalAlpha = node.banned ? 0.5 : 1;
    if (shadowColor !== 'transparent') {
      ctx.shadowColor = shadowColor;
      ctx.shadowBlur = 14;
      ctx.shadowOffsetY = 6;
    }

    ctx.beginPath();
    ctx.roundRect(x - W / 2, y - H / 2, W, H, NODE_RX);
    ctx.fillStyle = fillColor;
    ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.strokeStyle = outlineColor;
    ctx.lineWidth = outlineWidth;
    ctx.stroke();

    ctx.fillStyle = textColor;
    ctx.font = 'bold 11px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const words = node.title.toUpperCase().split(' ');
    const maxWidth = W - 20;
    const lineHeight = 13;
    const lines: string[] = [];
    let currentLine = '';

    words.forEach(word => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (ctx.measureText(testLine).width > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    });
    if (currentLine) lines.push(currentLine);

    const textStartY = y - ((lines.length - 1) * lineHeight) / 2;
    lines.forEach((line, i) => {
      if (node.banned) {
        const textWidth = ctx.measureText(line).width;
        ctx.fillText(line, x, textStartY + i * lineHeight);
        ctx.beginPath();
        ctx.moveTo(x - textWidth / 2, textStartY + i * lineHeight);
        ctx.lineTo(x + textWidth / 2, textStartY + i * lineHeight);
        ctx.strokeStyle = textColor;
        ctx.lineWidth = 1;
        ctx.stroke();
      } else {
        ctx.fillText(line, x, textStartY + i * lineHeight);
      }
    });

    ctx.restore();
  });

  if (hasPostIts) {
    ctx.save();
    ctx.strokeStyle = '#cdd6f4';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(nodesWidth, 0);
    ctx.lineTo(nodesWidth, height);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();

    let postItY = POST_IT_EXPORT_MARGIN;
    const postItX = nodesWidth + POST_IT_EXPORT_MARGIN;

    postIts.forEach((postIt, i) => {
      const { lines, h } = postItLayouts[i];
      const c = POST_IT_COLORS[postIt.color] || POST_IT_COLORS.yellow;

      ctx.save();
      ctx.fillStyle = 'rgba(0,0,0,0.07)';
      ctx.fillRect(postItX + 3, postItY + 5, POST_IT_EXPORT_W, h);
      ctx.fillStyle = c.fill;
      ctx.strokeStyle = c.stroke;
      ctx.lineWidth = 1;
      ctx.fillRect(postItX, postItY, POST_IT_EXPORT_W, h);
      ctx.strokeRect(postItX, postItY, POST_IT_EXPORT_W, h);
      ctx.fillStyle = c.text;
      ctx.font = '13px system-ui, sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      let lineY = postItY + 14;
      for (const line of lines) {
        ctx.fillText(line, postItX + 12, lineY);
        lineY += 19;
      }
      ctx.restore();

      postItY += h + POST_IT_EXPORT_MARGIN;
    });
  }

  canvas.toBlob(blob => {
    if (!blob) return;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'learnatlas-map.png';
    a.click();
  });
};

export const exportToMermaid = (nodes: Node[], connections: Connection[]): string => {
  const activeNodes = nodes.filter(n => !n.banned);
  let mermaid = EXPORT.MERMAID_PREFIX;

  activeNodes.forEach(node => {
    const shape = node.type === 'problem' ? '([' : '[';
    const endShape = node.type === 'problem' ? '])' : ']';
    mermaid += `  ${node.id}${shape}"${node.title}"${endShape}\n`;
  });

  connections.forEach(conn => {
    const fromNode = nodes.find(n => n.id === conn.from);
    const toNode = nodes.find(n => n.id === conn.to);
    if (fromNode && !fromNode.banned && toNode && !toNode.banned) {
      mermaid += `  ${conn.from} -.-> ${conn.to}\n`;
    }
  });

  return mermaid;
};

export const exportToJSON = (nodes: Node[], connections: Connection[], mapName = 'learnatlas-map', postIts: PostIt[] = []): void => {
  const data = { nodes, connections, postIts, exportedAt: new Date().toISOString() };
  const json = JSON.stringify(data, null, 2);
  downloadAsFile(json, `${mapName}.json`, 'application/json');
};

export const copyToClipboard = async (text: string): Promise<void> => {
  await navigator.clipboard.writeText(text);
};

export const downloadAsFile = (text: string, filename: string, mimeType = 'text/plain'): void => {
  const blob = new Blob([text], { type: mimeType });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
};
